import React, { useState, useEffect } from 'react';
import { FolderTree, MapPin, Plus, Loader2 } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api/v1';

interface Place {
  id: string;
  title: string;
  type: string;
  parent_id?: string | null;
  children?: Place[];
}

const TreeNode: React.FC<{ place: Place; depth?: number }> = ({ place, depth = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = place.children && place.children.length > 0;

  return (
    <div className="flex flex-col">
      <div 
        className="flex items-center py-2 px-3 hover:bg-slate-50 rounded-md cursor-pointer transition-colors"
        style={{ paddingLeft: `${depth * 1.5 + 0.75}rem` }}
        onClick={() => hasChildren && setIsExpanded(!isExpanded)}
      >
        <span className="mr-2 text-slate-400">
          {hasChildren ? (
            <FolderTree size={16} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          ) : (
            <MapPin size={16} />
          )}
        </span>
        <span className="text-slate-700 text-sm font-medium">{place.title}</span>
        <span className="ml-3 text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">
          {place.type}
        </span>
        <span className="ml-auto text-xs text-slate-400 font-mono">
          ID: {place.id}
        </span>
      </div>
      
      {isExpanded && hasChildren && (
        <div className="flex flex-col">
          {place.children!.map((child) => (
            <TreeNode key={child.id} place={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

export default function HeritageTree() {
  const [places, setPlaces] = useState<Place[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form State
  const [title, setTitle] = useState('');
  const [type, setType] = useState('Region');
  const [parentId, setParentId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchPlaces = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('jwt_token');
      const response = await fetch(`${API_URL}/heritage/places`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch places');
      
      const data = await response.json();
      
      if (Array.isArray(data)) {
        const rootNodes: Place[] = [];
        const map = new Map<string, Place>();
        
        data.forEach(item => {
          map.set(item.id, { ...item, children: [] });
        });
        
        data.forEach(item => {
          if (item.parent_id && map.has(item.parent_id)) {
            map.get(item.parent_id)!.children!.push(map.get(item.id)!);
          } else {
            rootNodes.push(map.get(item.id)!);
          }
        });
        
        setPlaces(rootNodes);
      } else {
        // Fallback for wrapped responses
        const placesArray = data.tree || data.places || [];
        if (placesArray && Array.isArray(placesArray)) {
          // Map backend format (uuid, name, children) to frontend format (id, title, children)
          const mapNode = (node: any): Place => ({
            id: node.uuid || node.id || Math.random().toString(),
            title: node.name || node.title || 'Unknown',
            type: node.type || 'Region',
            children: node.children ? node.children.map(mapNode) : []
          });

          if (data.tree) {
            // The API already built the tree and returned it as data.tree
            setPlaces(data.tree.map(mapNode));
          } else {
            // Fallback: build tree from flat list if needed
            const rootNodes: Place[] = [];
            const map = new Map<string, Place>();
            
            placesArray.forEach((item: any) => {
              const mapped = mapNode(item);
              map.set(mapped.id, mapped);
            });
            
            placesArray.forEach((item: any) => {
              const mapped = map.get(item.uuid || item.id)!;
              if (item.parent_id && map.has(item.parent_id)) {
                map.get(item.parent_id)!.children!.push(mapped);
              } else {
                rootNodes.push(mapped);
              }
            });
            
            setPlaces(rootNodes);
          }
        } else {
          if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
            setError(data.error || data.message || 'Unexpected response format from API');
            setPlaces([]);
          } else {
            setPlaces(Array.isArray(data) ? data : []);
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching places');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlaces();
  }, []);

  const handleAddPlace = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title) return;
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('jwt_token');
      const response = await fetch(`${API_URL}/heritage/places`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: title,
          type,
          parent_uuid: parentId || null
        })
      });
      
      if (!response.ok) throw new Error('Failed to create place');
      
      setTitle('');
      setType('Region');
      setParentId('');
      
      fetchPlaces();
    } catch (err: any) {
      alert(err.message || 'Error creating place');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div className="flex flex-col items-start gap-2">
        <h1 className="text-2xl font-semibold text-slate-900">Heritage Tree</h1>
        <p className="text-slate-500 text-sm">
          Manage the structural hierarchy of historical regions, cities, and landmarks.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="col-span-1">
          <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
            <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
              <Plus size={18} className="text-slate-400" />
              Add Place
            </h2>
            <form onSubmit={handleAddPlace} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                  placeholder="e.g. Damascus"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white"
                >
                  <option value="Region">Region</option>
                  <option value="City">City</option>
                  <option value="District">District</option>
                  <option value="Site">Site</option>
                  <option value="Landmark">Landmark</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Parent ID</label>
                <input
                  type="text"
                  value={parentId}
                  onChange={(e) => setParentId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                  placeholder="Optional parent ID"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : 'Create'}
              </button>
            </form>
          </div>
        </div>

        <div className="col-span-1 md:col-span-2">
          <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm min-h-[400px]">
            <h2 className="text-lg font-medium text-slate-800 mb-4">Places Hierarchy</h2>
            
            {loading ? (
              <div className="flex justify-center items-center h-48 text-slate-400">
                <Loader2 size={24} className="animate-spin" />
              </div>
            ) : error ? (
              <div className="text-red-500 text-sm p-4 bg-red-50 rounded-md border border-red-100">
                {error}
              </div>
            ) : places.length === 0 ? (
              <div className="text-slate-500 text-sm text-center py-12">
                No places found. Add one to get started.
              </div>
            ) : (
              <div className="border border-slate-100 rounded-md p-2 bg-slate-50/50">
                {places.map(place => (
                  <TreeNode key={place.id} place={place} />
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
